import { createApi } from '@reduxjs/toolkit/query/react';

import { createBaseQueryWithReauth } from '@/services';
import { API_BUSINESS_URL } from '@/utils';
import { TrackingIconUpdate } from '../models/TrackingIconForm';
import { PaginatedRequest, PaginatedResponse } from '@/types';

import {
  getTrackingIconAnchor,
  CurrentTrackingIcon,
  TrackingIcon,
  TrackingIconFilter,
  TrackingIconSlot,
} from '../models';

interface GetTrackingIconsRequest {
  paginated: PaginatedRequest;
  filters: TrackingIconFilter;
  slot: TrackingIconSlot;
}

const withTrackingIconDefaults = <
  T extends { isDefault: boolean; anchor?: unknown },
>(
  icon: T
): T => ({
  ...icon,
  isDefault: icon.isDefault ?? false,
  anchor: getTrackingIconAnchor(icon.anchor),
});

const trackingIconsService = createApi({
  reducerPath: 'trackingIconsService',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}/tracking-icons`),
  tagTypes: ['TrackingIcons'],
  endpoints: (builder) => ({
    getCurrentTrackingIcons: builder.query<CurrentTrackingIcon[], void>({
      query: () => ({ url: 'current' }),
      transformResponse: (icons: CurrentTrackingIcon[]) =>
        icons.map(withTrackingIconDefaults),
      providesTags: ['TrackingIcons'],
    }),
    getTrackingIconById: builder.query<TrackingIcon, string>({
      query: (id) => ({ url: id }),
      transformResponse: (icon: TrackingIcon) => withTrackingIconDefaults(icon),
      providesTags: ['TrackingIcons'],
    }),
    getTrackingIconAvailability: builder.query<
      TrackingIcon[],
      TrackingIconSlot
    >({
      async queryFn(slot, _api, _options, baseQuery) {
        const icons = new Map<string, TrackingIcon>();
        let offset = 0;
        let total = Infinity;
        while (offset < total) {
          const result = await baseQuery({
            url: '',
            params: { slot, enabled: true, limit: 100, offset },
          });
          if (result.error) return { error: result.error };
          const page = result.data as PaginatedResponse<TrackingIcon>;
          if (
            !Array.isArray(page?.items) ||
            !Number.isFinite(page.count) ||
            page.count < 0
          ) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'No se pudo comprobar la disponibilidad',
              },
            };
          }
          total = page.count;
          const previousSize = icons.size;
          for (const icon of page.items)
            icons.set(icon.id, withTrackingIconDefaults(icon));
          offset += page.items.length;
          if (
            (offset < total && icons.size === previousSize) ||
            icons.size !== offset ||
            offset > total
          ) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: 'No se pudo cargar la disponibilidad completa',
              },
            };
          }
        }
        return { data: [...icons.values()] };
      },
      providesTags: ['TrackingIcons'],
    }),
    createTrackingIcon: builder.mutation<TrackingIcon, FormData>({
      query: (data) => ({ url: '', method: 'POST', body: data }),
      transformResponse: (icon: TrackingIcon) => withTrackingIconDefaults(icon),
      invalidatesTags: ['TrackingIcons'],
    }),
    updateTrackingIcon: builder.mutation<
      TrackingIcon,
      { id: string; data: TrackingIconUpdate }
    >({
      query: ({ id, data }) => ({ url: id, method: 'PATCH', body: data }),
      transformResponse: (icon: TrackingIcon) => withTrackingIconDefaults(icon),
      invalidatesTags: ['TrackingIcons'],
    }),
    replaceTrackingIconImage: builder.mutation<
      TrackingIcon,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `${id}/image`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (icon: TrackingIcon) => withTrackingIconDefaults(icon),
      invalidatesTags: ['TrackingIcons'],
    }),
    disableTrackingIcon: builder.mutation<void, string>({
      query: (id) => ({ url: id, method: 'DELETE' }),
      invalidatesTags: ['TrackingIcons'],
    }),
    deleteTrackingIcon: builder.mutation<void, string>({
      query: (id) => ({ url: `${id}/permanent`, method: 'DELETE' }),
      invalidatesTags: ['TrackingIcons'],
    }),
    getTrackingIcons: builder.query<
      PaginatedResponse<TrackingIcon>,
      GetTrackingIconsRequest
    >({
      query: ({ paginated, filters, slot }) => ({
        url: '',
        params: {
          slot,
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(filters.name.trim() && { name: filters.name.trim() }),
          ...(filters.enabled && {
            enabled: filters.enabled === 'true',
          }),
          ...((filters.isDefault === 'true' ||
            filters.isDefault === 'false') && {
            isDefault: filters.isDefault === 'true',
          }),
        },
      }),
      transformResponse: (response: PaginatedResponse<TrackingIcon>) => ({
        ...response,
        items: response.items.map(withTrackingIconDefaults),
      }),
      providesTags: ['TrackingIcons'],
    }),
  }),
});

export default trackingIconsService;
export const {
  useGetCurrentTrackingIconsQuery,
  useGetTrackingIconsQuery,
  useGetTrackingIconByIdQuery,
  useLazyGetTrackingIconByIdQuery,
  useGetTrackingIconAvailabilityQuery,
  useLazyGetTrackingIconAvailabilityQuery,
  useCreateTrackingIconMutation,
  useUpdateTrackingIconMutation,
  useReplaceTrackingIconImageMutation,
  useDisableTrackingIconMutation,
  useDeleteTrackingIconMutation,
} = trackingIconsService;
